import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Sparkles } from 'lucide-react';
// No trailing ellipsis — the animated dots after the text carry the "in
// progress" sense, and a literal "…" next to them reads as a duplicate.
const STEPS = {
    analysis: [
        "Reading this ad's performance record",
        'Comparing CPOC, OCTR and ROAS against your benchmarks',
        'Checking the day-by-day trajectory for fatigue',
        'Looking at your top performers in this funnel',
        "Working out what's holding this ad back",
        'Drafting changes you can act on',
        'Putting it all together',
    ],
    reply: [
        'Thinking about your question',
        "Re-reading the ad's numbers",
        'Putting an answer together',
    ],
};
// Sized so several steps land inside the ~10s before streaming begins; at the
// old 7s the user only ever saw the first one.
const STEP_MS = 2600;
// Reaching this means no token has arrived long after one should have — the
// turn is genuinely slow, not just thinking.
const SLOW_AFTER_MS = 35000;
// Disabled wholesale under prefers-reduced-motion — the rotating text alone is
// enough to show progress, and motion here is decoration, not information.
const REDUCED = '@media (prefers-reduced-motion: reduce)';
const AiThinkingIndicator = ({ variant }) => {
    const steps = STEPS[variant];
    const [step, setStep] = useState(0);
    const [slow, setSlow] = useState(false);
    // The component mounts when the turn starts and unmounts when it ends, so
    // the timers reset naturally with no cleanup of their own state needed.
    useEffect(() => {
        const tick = window.setInterval(() => setStep((s) => Math.min(s + 1, steps.length - 1)), STEP_MS);
        const slowTimer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
        return () => {
            window.clearInterval(tick);
            window.clearTimeout(slowTimer);
        };
    }, [steps.length]);
    return (_jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'flex-start' }, children: [_jsxs(Box, { sx: { position: 'relative', width: 28, height: 28, flexShrink: 0 }, children: [_jsx(Box, { sx: {
                            position: 'absolute',
                            inset: -3,
                            borderRadius: '50%',
                            bgcolor: '#6172F3',
                            opacity: 0.25,
                            animation: 'aiHalo 1.8s ease-in-out infinite',
                            '@keyframes aiHalo': {
                                '0%, 100%': { transform: 'scale(0.85)', opacity: 0.12 },
                                '50%': { transform: 'scale(1.15)', opacity: 0.3 },
                            },
                            [REDUCED]: { animation: 'none', opacity: 0.15 },
                        } }), _jsx(Box, { sx: {
                            position: 'relative',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #6172F3 0%, #9E77ED 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'aiSpark 2.4s ease-in-out infinite',
                            '@keyframes aiSpark': {
                                '0%, 100%': { transform: 'rotate(0deg)' },
                                '50%': { transform: 'rotate(12deg)' },
                            },
                            [REDUCED]: { animation: 'none' },
                        }, children: _jsx(Sparkles, { size: 14, color: "#FFFFFF" }) })] }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: { fontSize: '12px', fontWeight: 600, color: '#344054', mb: 0.75 }, children: "G8A AI Agent" }), _jsxs(Box, { sx: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: '#FFFFFF',
                            border: '1px solid #EAECF0',
                            borderRadius: '2px 12px 12px 12px',
                            px: 1.5,
                            py: 1.25,
                        }, children: [_jsx(Typography, { sx: {
                                    fontSize: '13px',
                                    color: '#667085',
                                    animation: 'aiFade 420ms ease-out',
                                    '@keyframes aiFade': {
                                        from: { opacity: 0, transform: 'translateY(3px)' },
                                        to: { opacity: 1, transform: 'translateY(0)' },
                                    },
                                    [REDUCED]: { animation: 'none' },
                                }, children: steps[step] }, step), _jsx(Box, { sx: { display: 'flex', gap: '3px', pb: '2px', flexShrink: 0 }, children: [0, 1, 2].map((i) => (_jsx(Box, { sx: {
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        bgcolor: '#6172F3',
                                        animation: 'aiDot 1.2s ease-in-out infinite',
                                        animationDelay: `${i * 0.16}s`,
                                        '@keyframes aiDot': {
                                            '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
                                            '40%': { opacity: 1, transform: 'translateY(-3px)' },
                                        },
                                        [REDUCED]: { animation: 'none', opacity: 0.5 },
                                    } }, i))) })] }), slow && (_jsx(Typography, { sx: { fontSize: '11px', color: '#98A2B3', mt: 0.75, fontStyle: 'italic' }, children: "Taking longer than usual \u2014 still working on it." }))] })] }));
};
export default AiThinkingIndicator;
